import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pause, Pencil, Play, Plus, RotateCw, Send, Trash2, TriangleAlert } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo, type CorSelo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { ariaErro, useValidacaoLocal } from '../../componentes/validacao-local';
import { useToast } from '../../componentes/toast';
import { del, ErroRequisicao, get, mensagemDeErro, patch, post, textoDeErroGravado } from '../../nucleo/api';
import { ENTIDADE_WEBHOOK_WHATSAPP, textoLivreDoPayload } from '../../nucleo/censura-json';
import { rotuloEvento, rotuloModulo } from '../../nucleo/rotulos';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { dataHora } from '../../nucleo/formato';
import { BlocoCodigo, BotaoCopiar, Chips, Json, ListaPaginada, LinhaCopiavel, Quando, corDoStatusHttp } from './comum';
import type { Assinatura, Entrega, EventoCatalogo, EventoRecebido } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

const COR_ASSINATURA: Record<Assinatura['estado'], CorSelo> = { ativa: 'verde', pausada: 'laranja', desativada: 'perigo' };
const COR_ENTREGA: Record<Entrega['estado'], CorSelo> = { entregue: 'verde', pendente: 'azul', falhou: 'perigo', cancelada: 'neutro' };
const COR_RECEBIDO: Record<EventoRecebido['estado'], CorSelo> = { processado: 'verde', recebido: 'azul', erro: 'perigo', ignorado: 'neutro' };

/** Motivo de entrega cancelada, gravado em pt pelo worker (`MOTIVO_CANCELAMENTO`), para a chave de tradução. */
const MOTIVOS_CANCELAMENTO: Record<string, string> = {
  'assinatura removida': 'removida',
  'o evento não pertence mais à assinatura': 'eventoFora',
  'a URL da assinatura mudou depois que a entrega foi gerada': 'urlAlterada',
  'assinatura pausada ou desativada': 'inativa',
};

/** Motivo do cancelamento no idioma do app; motivo desconhecido volta como veio. */
function useMotivoCancelamento() {
  const { t } = useTranslation('integracoes');
  return (motivo: string | null | undefined) => (!motivo ? '' : Object.hasOwn(MOTIVOS_CANCELAMENTO, motivo) ? t(`webhooks.motivoCancelamento.${MOTIVOS_CANCELAMENTO[motivo]}`) : motivo);
}

/** Exemplo de validação com os comentários no idioma do app (r6 visual B6); o código em si é o mesmo nos três. */
const exemploValidacao = (c: { corpoCru: string; janela: string; assinaTimestamp: string; assinaCorpo: string }) => `import { createHmac, timingSafeEqual } from 'node:crypto';

// ${c.corpoCru}
function entregaValida(corpoCru, headers, segredo) {
  const ts = headers['x-erp-timestamp'];
  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) return false; // ${c.janela}
  // ${c.assinaTimestamp}
  // ${c.assinaCorpo}
  const esperado = 'sha256=' + createHmac('sha256', segredo).update(\`\${ts}.\${corpoCru}\`).digest('hex');
  const recebido = String(headers['x-erp-signature-timestamp'] ?? '');
  return recebido.length === esperado.length && timingSafeEqual(Buffer.from(recebido), Buffer.from(esperado));
}`;

function ComoValidar() {
  const { t } = useTranslation('integracoes');
  return (
    <Cartao titulo={t('webhooks.validar.titulo')}>
      <div className="flex min-w-0 flex-col gap-3 text-sm text-tinta-2">
        <p>{t('webhooks.validar.texto')}</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <code className="font-mono text-xs">X-ERP-Signature: sha256=&lt;hex&gt;</code>: {t('webhooks.validar.assinaturaCorpo')}
          </li>
          <li>
            <code className="font-mono text-xs">X-ERP-Signature-Timestamp: sha256=&lt;hex&gt;</code>: {t('webhooks.validar.assinatura')}
          </li>
          <li>
            <code className="font-mono text-xs">X-ERP-Timestamp</code>: {t('webhooks.validar.timestamp')}
          </li>
          <li>{t('webhooks.validar.retry')}</li>
        </ul>
        <BlocoCodigo codigo={exemploValidacao(t('webhooks.validar.codigo', { returnObjects: true }) as { corpoCru: string; janela: string; assinaTimestamp: string; assinaCorpo: string })} />
      </div>
    </Cartao>
  );
}

// ---------------------------------------------------------------------------
// Formulário de assinatura
// ---------------------------------------------------------------------------

function FormAssinatura({ editando, eventos, aoSalvar }: { editando: Assinatura | null; eventos: EventoCatalogo[]; aoSalvar: (segredo: string | null) => void }) {
  const { t } = useTranslation('integracoes');
  const qc = useQueryClient();
  const [nome, setNome] = useState(editando?.nome ?? '');
  const [url, setUrl] = useState(editando?.url ?? '');
  const [marcados, setMarcados] = useState<string[]>(editando?.eventos ?? []);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const porModulo = useMemo(() => {
    const m = new Map<string, { nome: string; lista: EventoCatalogo[] }>();
    for (const e of eventos) m.set(e.modulo, { nome: e.modulo_nome, lista: [...(m.get(e.modulo)?.lista ?? []), e] });
    return [...m.entries()];
  }, [eventos]);
  // Review r11 spec (B3): eventos de módulo desinstalado ficam fora do catálogo; aparecem à parte, marcados, para tirar.
  const orfaos = useMemo(() => {
    const doCatalogo = new Set(eventos.map((e) => e.evento));
    return [...new Set([...(editando?.eventos_orfaos ?? []), ...(editando?.eventos ?? []).filter((e) => !doCatalogo.has(e))])];
  }, [editando, eventos]);

  const { ref: refForm } = useValidacaoLocal(erros, erroGeral);
  const salvar = useMutation({
    mutationFn: async () => {
      const corpo = { nome: nome.trim() || null, url: url.trim(), eventos: marcados };
      if (editando) {
        await patch(`/integracoes/webhooks/${editando.id}`, corpo);
        return null;
      }
      return (await post<{ id: string; segredo: string }>('/integracoes/webhooks', corpo)).segredo;
    },
    onSuccess: async (segredo) => {
      await qc.invalidateQueries({ queryKey: ['integracoes', 'webhooks'] });
      aoSalvar(segredo);
    },
    onError: (e) => {
      if (e instanceof ErroRequisicao) {
        const porCampo = e.porCampo();
        setErros(porCampo);
        setErroGeral(Object.keys(porCampo).length ? null : mensagemDeErro(e));
      } else setErroGeral(mensagemDeErro(e));
    },
  });

  return (
    <form
      ref={refForm}
      id="form-assinatura"
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setErros({});
        setErroGeral(null);
        salvar.mutate();
      }}
    >
      {erroGeral && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
          {erroGeral}
        </p>
      )}
      <Rotulo id="wh-nome" rotulo={t('webhooks.campos.nome')} erro={erros.nome}>
        <Entrada id="wh-nome" {...ariaErro('wh-nome', erros.nome)} value={nome} maxLength={100} onChange={(e) => setNome(e.target.value)} />
      </Rotulo>
      <Rotulo id="wh-url" rotulo={t('webhooks.campos.url')} obrigatorio erro={erros.url} ajuda={t('webhooks.campos.urlAjuda')}>
        <Entrada id="wh-url" {...ariaErro('wh-url', erros.url)} type="url" inputMode="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
      </Rotulo>
      <fieldset className="flex min-w-0 flex-col gap-3">
        <legend className="text-sm font-medium text-tinta-2">
          {t('webhooks.campos.eventos')}
          <span className="ml-0.5 text-acento-forte">*</span>
        </legend>
        {erros.eventos && (
          <p id="wh-eventos-erro" className="text-xs text-perigo">
            {erros.eventos}
          </p>
        )}
        {orfaos.length > 0 && (
          <div className="flex flex-col gap-1.5" data-eventos-orfaos>
            <span className="kicker">{t('webhooks.orfaos.titulo')}</span>
            <p id="wh-orfaos-ajuda" className="text-xs text-tinta-2">
              {t('webhooks.orfaos.ajuda')}
            </p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {orfaos.map((ev) => (
                <label key={ev} className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-laranja bg-sup-2 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    data-evento-orfao={ev}
                    aria-describedby="wh-orfaos-ajuda"
                    checked={marcados.includes(ev)}
                    onChange={(x) => setMarcados((m) => (x.target.checked ? [...m, ev] : m.filter((y) => y !== ev)))}
                  />
                  <span className="min-w-0">
                    <TextoCortado className="block">{rotuloEvento(ev)}</TextoCortado>
                    <span className="block font-mono text-xs text-tinta-2">{ev}</span>
                    <span className="block text-xs text-tinta-2">{t('webhooks.orfaos.selo')}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
        {porModulo.map(([modulo, { nome: nomeModulo, lista }]) => (
          <div key={modulo} className="flex flex-col gap-1.5">
            <span className="kicker">{rotuloModulo(modulo, nomeModulo)}</span>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {lista.map((e) => (
                // r10 (dívida de web da segurança M1): evento de módulo que a pessoa não lê aparece desabilitado com o motivo (o
                // servidor recusaria a assinatura); um já marcado (assinatura antiga) continua desmarcável.
                // r11 visual M5: a opacidade de desabilitado vale só para a caixa e o nome do evento; o motivo é texto informativo,
                // com contraste AA (tinta-2, fora do elemento com opacidade).
                <label key={e.evento} className={`flex min-w-0 items-center gap-2 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm ${e.assinavel === false && !marcados.includes(e.evento) ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    className={e.assinavel === false && !marcados.includes(e.evento) ? 'opacity-70' : undefined}
                    type="checkbox"
                    {...(e.evento === eventos[0]?.evento ? ariaErro('wh-eventos', erros.eventos) : {})}
                    disabled={e.assinavel === false && !marcados.includes(e.evento)}
                    aria-describedby={e.assinavel === false ? `wh-sem-acesso-${e.evento}` : undefined}
                    checked={marcados.includes(e.evento)}
                    onChange={(x) => setMarcados((m) => (x.target.checked ? [...m, e.evento] : m.filter((y) => y !== e.evento)))}
                  />
                  <span className="min-w-0">
                    <span className={`block truncate ${e.assinavel === false && !marcados.includes(e.evento) ? 'opacity-70' : ''}`} title={e.evento}>
                      {rotuloEvento(e.evento)}
                      <span className="ml-2 font-mono text-xs text-tinta-3">{e.evento}</span>
                    </span>
                    {e.assinavel === false && (
                      <span id={`wh-sem-acesso-${e.evento}`} data-evento-sem-acesso className="block text-xs text-tinta-2">
                        {t('webhooks.semAcessoEvento', { modulo: rotuloModulo(e.modulo, e.modulo_nome) })}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </fieldset>
    </form>
  );
}

function SegredoModal({ segredo, aoFechar }: { segredo: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('integracoes');
  const { t: tn } = useTranslation();
  return (
    <Modal
      aberto={!!segredo}
      aoFechar={aoFechar}
      titulo={t('webhooks.segredo.titulo')}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-tinta-2">
        <p className="flex items-start gap-2 rounded-lg border border-laranja px-3 py-2">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
          <span>{t('webhooks.segredo.aviso')}</span>
        </p>
        {segredo && <LinhaCopiavel texto={segredo} />}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Entregas de uma assinatura
// ---------------------------------------------------------------------------

function DetalheEntrega({ entrega, aoFechar, criadaEm }: { entrega: Entrega | null; aoFechar: () => void; criadaEm: (id: string) => string | null }) {
  const { t } = useTranslation('integracoes');
  const motivoCancelamento = useMotivoCancelamento();
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const reenviar = useMutation({
    mutationFn: (id: string) => post(`/integracoes/entregas/${id}/reenviar`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['integracoes', 'entregas'] });
      toast.sucesso(t('webhooks.toast.reenviada'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto={!!entrega}
      aoFechar={aoFechar}
      largura="lg"
      titulo={entrega ? rotuloEvento(entrega.evento) : ''}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.fechar')}
          </Botao>
          {entrega && podeNoWorkspace(s, 'integracoes.webhooks') && (
            <Botao variante="primario" icone={<RotateCw className="size-4" aria-hidden />} title={t('webhooks.acoes.reenviarDica')} aria-describedby="entrega-reenviar-dica" carregando={reenviar.isPending} onClick={() => reenviar.mutate(entrega.id)}>
              {t('webhooks.acoes.reenviar')}
            </Botao>
          )}
        </>
      }
    >
      {entrega && (
        <div className="flex min-w-0 flex-col gap-4 text-sm">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <dt className="kicker">{t('webhooks.entrega.estado')}</dt>
              <dd className="mt-1">
                <Selo cor={COR_ENTREGA[entrega.estado]}>{t(`webhooks.entregaEstado.${entrega.estado}`)}</Selo>
              </dd>
            </div>
            <div>
              <dt className="kicker">{t('webhooks.entrega.tentativa')}</dt>
              <dd className="numero mt-1">{entrega.tentativa}</dd>
            </div>
            <div>
              <dt className="kicker">{t('webhooks.entrega.http')}</dt>
              <dd className="mt-1">{entrega.status_http === null ? '-' : <Selo cor={corDoStatusHttp(entrega.status_http)}>{entrega.status_http}</Selo>}</dd>
            </div>
            <div>
              <dt className="kicker">{t('webhooks.entrega.proxima')}</dt>
              <dd className="mt-1">
                <Quando v={entrega.proxima_tentativa} />
              </dd>
            </div>
          </dl>
          {/* Review r12 robustez (M1): a entrega nova que reenvia uma já entregue mostra o vínculo com a original. */}
          {entrega.reenvio_de && (
            <p data-entrega-reenvio className="text-sm text-tinta-2">
              <ReenvioDe entrega={entrega} criadaEm={criadaEm} />
            </p>
          )}
          {/* Review r11 segurança (M1): a entrega cancelada sem POST diz por quê (evento saiu da assinatura, URL mudou...). */}
          {entrega.estado === 'cancelada' && entrega.motivo && (
            <p role="note" data-entrega-motivo className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-1">
              <span className="font-medium">{t('webhooks.entrega.motivo')}:</span> {motivoCancelamento(entrega.motivo)}
            </p>
          )}
          {/* r10 (dívida de web da segurança M1): o servidor não mandou o conteúdo, ou parte dele, pela permissão de quem lê. */}
          {entrega.ocultado && (
            <p role="note" data-entrega-ocultado={entrega.ocultado} className="rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-1">
              {t(entrega.ocultado === 'conteudo' ? 'webhooks.entrega.ocultadoConteudo' : 'webhooks.entrega.ocultadoCampos')}
            </p>
          )}
          {podeNoWorkspace(s, 'integracoes.webhooks') && (
            <p id="entrega-reenviar-dica" className="text-xs text-tinta-3">
              {t('webhooks.acoes.reenviarDica')}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <span className="kicker">{t('webhooks.entrega.payload')}</span>
            {entrega.payload === null && entrega.ocultado === 'conteudo' ? <span className="text-tinta-3">{t('webhooks.entrega.conteudoOculto')}</span> : <Json valor={entrega.payload} />}
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="kicker">{t('webhooks.entrega.resposta')}</span>
            {entrega.ocultado === 'conteudo' && !entrega.resposta ? (
              <span className="text-tinta-3">{t('webhooks.entrega.conteudoOculto')}</span>
            ) : entrega.resposta ? <pre className="max-h-48 max-w-full overflow-auto rounded-lg border border-borda bg-sup-2 p-3 font-mono text-xs break-words whitespace-pre-wrap">{entrega.resposta}</pre> : <span className="text-tinta-3">{t('webhooks.entrega.semResposta')}</span>}
          </div>
        </div>
      )}
    </Modal>
  );
}

/** "Reenvio da entrega de dd/mm/aaaa hh:mm" (review r12 robustez M1); sem a original carregada, sem a data. */
function ReenvioDe({ entrega, criadaEm }: { entrega: Entrega; criadaEm: (id: string) => string | null }) {
  const { t } = useTranslation('integracoes');
  const quando = entrega.reenvio_de ? criadaEm(entrega.reenvio_de) : null;
  return <>{quando ? t('webhooks.entrega.reenvioDe', { quando: dataHora(quando) }) : t('webhooks.entrega.reenvioDeAnterior')}</>;
}

function Entregas({ assinatura, aoFechar }: { assinatura: Assinatura | null; aoFechar: () => void }) {
  const { t } = useTranslation('integracoes');
  const motivoCancelamento = useMotivoCancelamento();
  const { t: tn } = useTranslation();
  const [aberta, setAberta] = useState<Entrega | null>(null);
  const [estado, setEstado] = useState('');
  const qc = useQueryClient();
  // Data da entrega original de um reenvio, lida das páginas já carregadas (com ou sem filtro de estado).
  const criadaEm = (id: string) => {
    for (const [, d] of qc.getQueriesData<{ pages: { itens: Entrega[] }[] }>({ queryKey: ['integracoes', 'entregas', assinatura?.id] }))
      for (const p of d?.pages ?? []) for (const e of p.itens) if (e.id === id) return e.created_at;
    return null;
  };
  return (
    <Drawer aberto={!!assinatura} aoFechar={aoFechar} largura="lg" titulo={`${t('webhooks.acoes.entregas')}: ${assinatura?.nome || assinatura?.url || ''}`}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      {assinatura && (
        <div className="flex min-w-0 flex-col gap-3">
          <div className="w-48">
            <Selecao
              aria-label={t('webhooks.entrega.estado')}
              vazio={t('webhooks.todosEstados')}
              opcoes={(['pendente', 'entregue', 'falhou', 'cancelada'] as const).map((e) => ({ valor: e, rotulo: t(`webhooks.entregaEstado.${e}`) }))}
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
            />
          </div>
          <div className="min-w-0 rounded-xl border border-borda bg-sup-1">
            <ListaPaginada<Entrega>
              chaveConsulta={['integracoes', 'entregas', assinatura.id]}
              caminho={`/integracoes/webhooks/${assinatura.id}/entregas`}
              params={{ estado: estado || undefined }}
              vazio={t('webhooks.vazioEntregas')}
              aoClicar={setAberta}
              colunas={[
                { chave: 'evento', rotulo: t('webhooks.entrega.evento'), render: (e) => <span title={e.evento}>{rotuloEvento(e.evento)}</span> },
                {
                  chave: 'estado',
                  rotulo: t('webhooks.entrega.estado'),
                  render: (e) => (
                    <span className="flex min-w-0 flex-col items-start gap-0.5">
                      <Selo cor={COR_ENTREGA[e.estado]}>{t(`webhooks.entregaEstado.${e.estado}`)}</Selo>
                      {e.estado === 'cancelada' && e.motivo && (
                        <span data-entrega-motivo className="text-xs text-tinta-2">
                          {motivoCancelamento(e.motivo)}
                        </span>
                      )}
                      {e.reenvio_de && (
                        <span data-entrega-reenvio className="text-xs text-tinta-2">
                          <ReenvioDe entrega={e} criadaEm={criadaEm} />
                        </span>
                      )}
                    </span>
                  ),
                },
                { chave: 'http', rotulo: t('webhooks.entrega.http'), render: (e) => (e.status_http === null ? '-' : <Selo cor={corDoStatusHttp(e.status_http)}>{e.status_http}</Selo>) },
                { chave: 'tentativa', rotulo: t('webhooks.entrega.tentativa'), render: (e) => <span className="numero">{e.tentativa}</span> },
                { chave: 'proxima', rotulo: t('webhooks.entrega.proxima'), render: (e) => <Quando v={e.proxima_tentativa} /> },
                { chave: 'criada', rotulo: t('webhooks.entrega.criadaEm'), render: (e) => <Quando v={e.created_at} /> },
              ]}
            />
          </div>
        </div>
      )}
      <DetalheEntrega entrega={aberta} aoFechar={() => setAberta(null)} criadaEm={criadaEm} />
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Saída
// ---------------------------------------------------------------------------

function CartaoAssinatura({ a, aoEditar, aoEntregas }: { a: Assinatura; aoEditar: () => void; aoEntregas: () => void }) {
  const { t } = useTranslation('integracoes');
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const [removendo, setRemovendo] = useState(false);
  const pode = podeNoWorkspace(s, 'integracoes.webhooks');
  const invalidar = () => qc.invalidateQueries({ queryKey: ['integracoes', 'webhooks'] });
  const erro = (e: unknown) => toast.erro(mensagemDeErro(e));

  const alternar = useMutation({
    mutationFn: () => post(`/integracoes/webhooks/${a.id}/${a.estado === 'ativa' ? 'pausar' : 'reativar'}`),
    onSuccess: async () => {
      await invalidar();
      toast.sucesso(t(a.estado === 'ativa' ? 'webhooks.toast.pausada' : 'webhooks.toast.reativada'));
    },
    onError: erro,
  });
  const testar = useMutation({
    mutationFn: () => post(`/integracoes/webhooks/${a.id}/teste`),
    onSuccess: async () => {
      toast.sucesso(t('webhooks.toast.testeEnviado'));
      setTimeout(() => void invalidar(), 3000);
    },
    onError: erro,
  });
  const remover = useMutation({
    mutationFn: () => del(`/integracoes/webhooks/${a.id}`),
    onSuccess: async () => {
      setRemovendo(false);
      await invalidar();
      toast.sucesso(t('webhooks.toast.removida'));
    },
    onError: erro,
  });

  return (
    <Cartao>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <TextoCortado como="h2" className="font-semibold">{a.nome || a.url}</TextoCortado>
          <p className="numero mt-0.5 truncate text-xs text-tinta-3" title={a.url}>
            {a.url}
          </p>
        </div>
        <Selo cor={COR_ASSINATURA[a.estado]}>{t(`webhooks.estado.${a.estado}`)}</Selo>
      </div>
      <div className="mt-3 flex flex-col gap-2 text-sm">
        <Chips itens={a.eventos.map(rotuloEvento)} max={5} />
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-tinta-3">
          <span>
            {t('webhooks.campos.falhas')}: <span className="numero text-tinta-2">{a.falhas_seguidas}</span>
          </span>
          <span>
            {t('webhooks.campos.entregas')}: <span className="numero text-tinta-2">{a.entregas_total}</span>
          </span>
          <span>
            {t('webhooks.campos.ultimaEntrega')}: <Quando v={a.ultima_entrega} />
          </span>
          {a.ultimo_status !== null && <Selo cor={corDoStatusHttp(a.ultimo_status)}>{a.ultimo_status}</Selo>}
        </div>
        {(a.eventos_orfaos?.length ?? 0) > 0 && (
          <p role="note" data-assinatura-orfaos className="flex items-start gap-2 rounded-lg border border-laranja px-3 py-2 text-xs text-tinta-1">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
            <span>
              {t('webhooks.orfaos.aviso', { count: a.eventos_orfaos!.length })} <span className="font-mono">{a.eventos_orfaos!.join(', ')}</span>
            </span>
          </p>
        )}
        {a.estado === 'desativada' && (
          <p role="alert" className="flex items-start gap-2 rounded-lg border border-perigo px-3 py-2 text-xs text-perigo">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t('webhooks.desativadaAviso')}
          </p>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2 border-t border-borda pt-4">
        <Botao onClick={aoEntregas}>{t('webhooks.acoes.entregas')}</Botao>
        {pode && (
          <>
            <Botao icone={<Send className="size-4" aria-hidden />} carregando={testar.isPending} disabled={a.estado !== 'ativa'} onClick={() => testar.mutate()}>
              {t('webhooks.acoes.testar')}
            </Botao>
            <Botao icone={a.estado === 'ativa' ? <Pause className="size-4" aria-hidden /> : <Play className="size-4" aria-hidden />} carregando={alternar.isPending} onClick={() => alternar.mutate()}>
              {a.estado === 'ativa' ? t('webhooks.acoes.pausar') : t('webhooks.acoes.reativar')}
            </Botao>
            <Botao variante="fantasma" icone={<Pencil className="size-4" aria-hidden />} onClick={aoEditar}>
              {t('webhooks.acoes.editar')}
            </Botao>
            <Botao variante="perigo" aria-label={t('webhooks.acoes.remover')} icone={<Trash2 className="size-4" aria-hidden />} onClick={() => setRemovendo(true)} />
          </>
        )}
      </div>
      <Confirmar
        aberto={removendo}
        aoFechar={() => setRemovendo(false)}
        aoConfirmar={() => remover.mutate()}
        carregando={remover.isPending}
        perigoso
        titulo={t('webhooks.confirmar.remover')}
        texto={t('webhooks.confirmar.removerTexto')}
      />
    </Cartao>
  );
}

function Saida() {
  const { t } = useTranslation('integracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [form, setForm] = useState<Assinatura | 'nova' | null>(null);
  const [segredo, setSegredo] = useState<string | null>(null);
  const [entregas, setEntregas] = useState<Assinatura | null>(null);
  const lista = useQuery({ queryKey: ['integracoes', 'webhooks'], queryFn: () => get<{ itens: Assinatura[] }>('/integracoes/webhooks') });
  const catalogo = useQuery({ queryKey: ['integracoes', 'webhooks', 'eventos'], queryFn: () => get<{ itens: EventoCatalogo[] }>('/integracoes/webhooks/eventos') });

  return (
    <div className="flex flex-col gap-4">
      {podeNoWorkspace(s, 'integracoes.webhooks') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" aria-hidden />} onClick={() => setForm('nova')}>
            {t('webhooks.acoes.nova')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={lista}>
        {(d) =>
          d.itens.length === 0 ? (
            <div className="rounded-xl border border-borda bg-sup-1">
              <Vazio titulo={t('webhooks.vazioTitulo')} texto={t('webhooks.vazio')} />
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {d.itens.map((a) => (
                <CartaoAssinatura key={a.id} a={a} aoEditar={() => setForm(a)} aoEntregas={() => setEntregas(a)} />
              ))}
            </div>
          )
        }
      </Estado>
      <ComoValidar />

      <Drawer
        aberto={!!form}
        aoFechar={() => setForm(null)}
        titulo={form && form !== 'nova' ? t('webhooks.acoes.editar') : t('webhooks.acoes.nova')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setForm(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-assinatura">
              {t('webhooks.acoes.salvar')}
            </Botao>
          </>
        }
      >
        {form && catalogo.data && (
          <FormAssinatura
            key={form === 'nova' ? 'nova' : form.id}
            editando={form === 'nova' ? null : form}
            eventos={catalogo.data.itens}
            aoSalvar={(seg) => {
              setForm(null);
              if (seg) setSegredo(seg);
            }}
          />
        )}
      </Drawer>
      <SegredoModal segredo={segredo} aoFechar={() => setSegredo(null)} />
      <Entregas assinatura={entregas} aoFechar={() => setEntregas(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entrada
// ---------------------------------------------------------------------------

// Review r21 spec (B7): os dois gateways de WhatsApp não oficial também (rótulo na lista, no detalhe e no filtro).
const CONECTORES_ENTRADA = ['whatsapp', 'whatsapp-baileys', 'whatsapp-whatsmeow', 'instagram', 'asaas', 'sicredi-pix', 'landing', 'clicksign', 'documenso', 'jitsi', 'zernio', 'maton'] as const;
/** Review r21 spec (M2): o payload dos conectores de WhatsApp tem telefone e nome nas chaves do protocolo (modo discreto). */
const entidadeDoPayload = (conector: string) => (conector === 'whatsapp' || conector.startsWith('whatsapp-') ? ENTIDADE_WEBHOOK_WHATSAPP : null);
/**
 * Review r22 robustez (O2): o erro gravado é texto livre. No modo discreto, telefone, e-mail e documento somem dele, e os nomes
 * que o próprio payload traz (contato do WhatsApp, `profile.name`, `push_name`) viram iniciais, na lista e no detalhe.
 */
const erroDoEvento = (e: EventoRecebido) => textoLivreDoPayload(textoDeErroGravado(e.erro), e.payload, entidadeDoPayload(e.conector));

function EntradaEventos() {
  const { t } = useTranslation('integracoes');
  const [conector, setConector] = useState('');
  const [estado, setEstado] = useState('');
  const [aberto, setAberto] = useState<EventoRecebido | null>(null);
  const { t: tn } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <p className="max-w-3xl text-sm text-tinta-3">{t('webhooks.entrada.intro')}</p>
      <div className="flex flex-wrap gap-2">
        <div className="w-48">
          <Selecao aria-label={t('webhooks.entrada.conector')} vazio={t('webhooks.entrada.todosConectores')} opcoes={CONECTORES_ENTRADA.map((c) => ({ valor: c, rotulo: t(`webhooks.entrada.conectores.${c}`) }))} value={conector} onChange={(e) => setConector(e.target.value)} />
        </div>
        <div className="w-48">
          <Selecao aria-label={t('webhooks.entrada.estado')} vazio={t('webhooks.todosEstados')} opcoes={(['recebido', 'processado', 'erro', 'ignorado'] as const).map((e) => ({ valor: e, rotulo: t(`webhooks.entrada.estados.${e}`) }))} value={estado} onChange={(e) => setEstado(e.target.value)} />
        </div>
      </div>
      <div className="min-w-0 rounded-xl border border-borda bg-sup-1">
        <ListaPaginada<EventoRecebido>
          chaveConsulta={['integracoes', 'eventos-recebidos']}
          caminho="/integracoes/eventos-recebidos"
          params={{ conector: conector || undefined, estado: estado || undefined }}
          vazio={t('webhooks.entrada.vazio')}
          aoClicar={setAberto}
          colunas={[
            { chave: 'conector', rotulo: t('webhooks.entrada.conector'), render: (e) => <span className="font-medium">{t(`webhooks.entrada.conectores.${e.conector}`, { defaultValue: e.conector })}</span> },
            { chave: 'estado', rotulo: t('webhooks.entrada.estado'), render: (e) => <Selo cor={COR_RECEBIDO[e.estado]}>{t(`webhooks.entrada.estados.${e.estado}`)}</Selo> },
            { chave: 'id_externo', rotulo: t('webhooks.entrada.idExterno'), render: (e) => <span className="numero text-xs">{e.id_externo}</span> },
            { chave: 'erro', rotulo: t('webhooks.entrada.erro'), render: (e) => <span className="text-xs text-perigo">{erroDoEvento(e)}</span> },
            { chave: 'quando', rotulo: t('webhooks.entrada.recebidoEm'), render: (e) => <Quando v={e.created_at} /> },
          ]}
        />
      </div>
      <Modal
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={aberto ? `${t(`webhooks.entrada.conectores.${aberto.conector}`, { defaultValue: aberto.conector })} (${aberto.id_externo})` : ''}
        rodape={
          <>
            {aberto && aberto.ocultado !== 'conteudo' && <BotaoCopiar sensivel texto={JSON.stringify(aberto.payload, null, 2)} rotulo={t('webhooks.entrada.copiarPayload')} tamanho="md" />}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.fechar')}
            </Botao>
          </>
        }
      >
        {aberto && (
          <div className="flex min-w-0 flex-col gap-4 text-sm">
            <div className="flex flex-wrap items-center gap-3">
              <Selo cor={COR_RECEBIDO[aberto.estado]}>{t(`webhooks.entrada.estados.${aberto.estado}`)}</Selo>
              <Quando v={aberto.created_at} />
            </div>
            {aberto.erro && (
              <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-perigo">
                {erroDoEvento(aberto)}
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <span className="kicker">{t('webhooks.entrega.payload')}</span>
              {aberto.ocultado === 'conteudo' ? <span className="text-tinta-3">{t('webhooks.entrega.conteudoOculto')}</span> : <Json valor={aberto.payload} entidade={entidadeDoPayload(aberto.conector)} />}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function Webhooks() {
  const { t } = useTranslation('integracoes');
  const [parte, setParte] = useState<'saida' | 'entrada'>('saida');
  return (
    <div className="flex flex-col gap-4">
      <Abas idBase="webhooks-partes" rotulo={t('abas.webhooks')} ativa={parte} aoMudar={setParte} itens={(['saida', 'entrada'] as const).map((p) => ({ chave: p, rotulo: t(`webhooks.partes.${p}`) }))} />
      <PainelAba idBase="webhooks-partes" chave={parte} className="flex flex-col gap-4">
        {parte === 'saida' ? <Saida /> : <EntradaEventos />}
      </PainelAba>
    </div>
  );
}
